import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';
interface MultiSelectProps {
  label: string;
  options: string[] | {
    label: string;
    value: string;
    category?: string;
  }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  grouped?: boolean;
}
export function MultiSelect({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  grouped = false
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };
  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  };
  const handleSelectAll = () => {
    if (grouped && typeof options[0] === 'object') {
      const allValues = (options as {
        value: string;
      }[]).map(opt => opt.value);
      onChange(allValues);
    } else {
      onChange(options as string[]);
    }
  };
  // Group options by category if grouped
  const groupedOptions = grouped && typeof options[0] === 'object' ? (options as {
    label: string;
    value: string;
    category?: string;
  }[]).reduce((acc, opt) => {
    const category = opt.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(opt);
    return acc;
  }, {} as Record<string, {
    label: string;
    value: string;
  }[]>) : null;
  const getDisplayLabel = (value: string) => {
    if (grouped && typeof options[0] === 'object') {
      const opt = (options as {
        label: string;
        value: string;
      }[]).find(o => o.value === value);
      return opt?.label || value;
    }
    return value;
  };
  return <div className="relative" ref={dropdownRef} data-id="element-244">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-245">
        {label}
      </label>

      <button type="button" onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled} className={`w-full min-h-[42px] px-3 py-2 text-left bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}`} aria-haspopup="listbox" aria-expanded={isOpen} data-id="element-246">
        <div className="flex items-center justify-between gap-2" data-id="element-247">
          <div className="flex flex-wrap gap-1 flex-1" data-id="element-248">
            {selectedValues.length === 0 ? <span className="text-gray-400" data-id="element-249">{placeholder}</span> : selectedValues.map(value => <span key={value} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded" data-id="element-250">
                  {getDisplayLabel(value)}
                  <button type="button" onClick={e => handleRemove(value, e)} className="hover:text-blue-900" aria-label={`Remove ${value}`} data-id="element-251">
                    <X className="w-3 h-3" data-id="element-252" />
                  </button>
                </span>)}
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-253" />
        </div>
      </button>

      {isOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-254">
          {options.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500" data-id="element-255">
              No options available
            </div> : <>
              {/* Select All Button */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2" data-id="element-256">
                <button type="button" onClick={handleSelectAll} className="w-full px-3 py-2 text-sm font-medium text-white bg-[#0047AB] hover:bg-[#003580] rounded transition-colors" data-id="element-257">
                  Select All
                </button>
              </div>

              {/* Options List */}
              <div role="listbox" className="py-1" data-id="element-258">
                {groupedOptions ? Object.entries(groupedOptions).map(([category, categoryOptions]) => <div key={category} data-id="element-259">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50" data-id="element-260">
                            {category}
                          </div>
                          {categoryOptions.map(option => <label key={option.value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-261">
                              <input type="checkbox" checked={selectedValues.includes(option.value)} onChange={() => handleToggle(option.value)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-262" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-263">
                                {option.label}
                              </span>
                            </label>)}
                        </div>) : (options as string[]).map(option => <label key={option} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-264">
                        <input type="checkbox" checked={selectedValues.includes(option)} onChange={() => handleToggle(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-265" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-266">
                          {option}
                        </span>
                      </label>)}
              </div>
            </>}
        </div>}
    </div>;
}